/**
 * TradeExecutionBreakdown - Shows how a market order was filled across price levels.
 * Helps users understand slippage and average execution price.
 */

import { memo, useMemo } from 'react'
import { X, TrendingUp, Info } from 'lucide-react'
import { cn } from '../../lib/utils'
import type { Trade } from '../../engine/types'

export interface TradeExecutionBreakdownProps {
  /** The trades that made up this execution */
  trades: Trade[]
  /** The side of the order (buy or sell) */
  side: 'buy' | 'sell'
  /** Total quantity requested */
  totalQuantity: number
  /** Callback to dismiss the breakdown */
  onDismiss: () => void
  /** Optional className */
  className?: string
}

/**
 * TradeExecutionBreakdown visualizes how a market order was filled.
 */
export const TradeExecutionBreakdown = memo(function TradeExecutionBreakdown({
  trades,
  side,
  totalQuantity,
  onDismiss,
  className,
}: TradeExecutionBreakdownProps) {
  // Calculate execution statistics
  const stats = useMemo(() => {
    if (trades.length === 0) return null

    const totalCost = trades.reduce((sum, t) => sum + t.price * t.quantity, 0)
    const totalFilled = trades.reduce((sum, t) => sum + t.quantity, 0)
    const avgPrice = totalCost / totalFilled
    const bestPrice = side === 'buy'
      ? Math.min(...trades.map(t => t.price))
      : Math.max(...trades.map(t => t.price))
    const worstPrice = side === 'buy'
      ? Math.max(...trades.map(t => t.price))
      : Math.min(...trades.map(t => t.price))
    const slippage = Math.abs(worstPrice - bestPrice)

    return {
      totalCost,
      totalFilled,
      avgPrice,
      bestPrice,
      worstPrice,
      slippage,
      priceRange: trades.length > 1,
    }
  }, [trades, side])

  // Group trades by price level
  const priceLevels = useMemo(() => {
    const levels = new Map<number, number>()
    for (const trade of trades) {
      const existing = levels.get(trade.price) ?? 0
      levels.set(trade.price, existing + trade.quantity)
    }
    // Sort by price (ascending for buys to show best price first)
    return Array.from(levels.entries())
      .sort((a, b) => side === 'buy' ? a[0] - b[0] : b[0] - a[0])
  }, [trades, side])

  if (!stats || trades.length === 0) return null

  const isBuy = side === 'buy'
  const wasPartialFill = stats.totalFilled < totalQuantity

  return (
    <div
      className={cn(
        'rounded-lg border bg-card p-4 shadow-lg',
        isBuy ? 'border-green-500/30' : 'border-red-500/30',
        className
      )}
      role="alert"
      aria-live="polite"
    >
      {/* Header */}
      <div className="mb-3 flex items-start justify-between">
        <div className="flex items-center gap-2">
          <div className={cn(
            'flex h-8 w-8 items-center justify-center rounded-full',
            isBuy ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'
          )}>
            <TrendingUp className="h-4 w-4" />
          </div>
          <div>
            <h3 className="text-sm font-medium text-foreground">
              Order {wasPartialFill ? 'Partially ' : ''}Filled
            </h3>
            <p className="text-xs text-muted-foreground">
              {stats.totalFilled} of {totalQuantity} shares {isBuy ? 'bought' : 'sold'}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Price breakdown table */}
      <div className="mb-3 rounded-md border border-border bg-muted/30">
        <div className="border-b border-border px-3 py-2">
          <p className="text-xs font-medium text-muted-foreground">
            Execution Breakdown
          </p>
        </div>
        <div className="divide-y divide-border">
          {priceLevels.map(([price, quantity], idx) => {
            const levelCost = price * quantity
            const percentage = (quantity / stats.totalFilled) * 100
            return (
              <div
                key={price}
                className="flex items-center justify-between px-3 py-2"
              >
                <div className="flex items-center gap-3">
                  <span className="text-xs text-muted-foreground">
                    {idx + 1}.
                  </span>
                  <div>
                    <span className="font-mono text-sm tabular-nums text-foreground">
                      {quantity} shares
                    </span>
                    <span className="mx-1 text-xs text-muted-foreground">@</span>
                    <span className="font-mono text-sm tabular-nums text-foreground">
                      {Math.round(price * 100)}¢
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <span className="font-mono text-sm tabular-nums text-foreground">
                    ${levelCost.toFixed(2)}
                  </span>
                  <span className="ml-2 text-xs text-muted-foreground">
                    ({percentage.toFixed(0)}%)
                  </span>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Summary stats */}
      <div className="space-y-2 rounded-md bg-muted/50 p-3">
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Total Cost:</span>
          <span className="font-medium tabular-nums text-foreground">
            ${stats.totalCost.toFixed(2)}
          </span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Average Price:</span>
          <span className="font-medium tabular-nums text-foreground">
            {Math.round(stats.avgPrice * 100)}¢ per share
          </span>
        </div>
        {stats.priceRange && (
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Price Range:</span>
            <span className="font-medium tabular-nums text-foreground">
              {Math.round(stats.bestPrice * 100)}¢ – {Math.round(stats.worstPrice * 100)}¢
            </span>
          </div>
        )}
      </div>

      {/* Educational note about slippage */}
      {stats.priceRange && (
        <div className="mt-3 flex items-start gap-2 rounded-md bg-amber-500/10 p-3">
          <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600 dark:text-amber-400" />
          <div className="text-xs text-amber-700 dark:text-amber-300">
            <strong>What is slippage?</strong> Your order was filled across multiple price levels
            because there weren't enough shares available at the best price. The difference
            between the best price ({Math.round(stats.bestPrice * 100)}¢) and worst price
            ({Math.round(stats.worstPrice * 100)}¢) is called "slippage" ({Math.round(stats.slippage * 100)}¢).
          </div>
        </div>
      )}
    </div>
  )
})
